import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Plus, Clock, Square, FolderKanban, Users as UsersIcon, CalendarClock } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { SearchInput } from '@/components/SearchInput';
import { StatusPill } from '@/components/StatusPill';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { useLabels, useCompanyCurrency, useCurrencyField } from '@/contexts/WorkspaceContext';
import { projectsService, Project, ProjectBillingType, ProjectStatus, ActiveTimer } from '@/services/projects.service';
import { contactsService } from '@/services/contacts.service';
import { settingsService } from '@/services/settings.service';
import { useModuleAccess } from '@/contexts/ModuleAccessContext';

const STATUS_OPTIONS: Array<{ value: ProjectStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_VARIANT: Record<ProjectStatus, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  planning: 'info', active: 'success', on_hold: 'warning', completed: 'neutral', cancelled: 'danger',
};

const COLOR_SWATCHES = ['#a18a44', '#2563eb', '#7c3aed', '#0d9488', '#dc2626', '#d97706', '#4b5563'];

export default function ProjectsList() {
  const { has } = useModuleAccess();
  const timeEnabled = has('time_tracking');
  const contactsEnabled = has('contacts');
  const t = useLabels();
  const companyCurrency = useCompanyCurrency();
  const [projects, setProjects] = useState<Project[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<ProjectStatus | ''>('');
  const [contacts, setContacts] = useState<Array<{ contactUid: string; firstName: string; lastName?: string }>>([]);
  const [team, setTeam] = useState<Array<{ userUid: string; firstName: string; lastName?: string; email: string }>>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [timer, setTimer] = useState<{ running: boolean; timer: ActiveTimer | null }>({ running: false, timer: null });
  const [elapsed, setElapsed] = useState(0);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [clientContactUid, setClientContactUid] = useState('');
  const [billingType, setBillingType] = useState<ProjectBillingType>('non_billable');
  const [budgetAmount, setBudgetAmount] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [budgetHours, setBudgetHours] = useState('');
  const [currency, setCurrency] = useCurrencyField();
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [ownerUserUid, setOwnerUserUid] = useState('');
  const [color, setColor] = useState(COLOR_SWATCHES[0]);

  const contactName = (uid?: string) => {
    if (!uid) return null;
    const c = contacts.find((x) => x.contactUid === uid);
    return c ? [c.firstName, c.lastName].filter(Boolean).join(' ') : null;
  };

  const refresh = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const r = await projectsService.list({ q: q || undefined, status: status || undefined, limit: 100 });
      setProjects(r.items);
      setTotal(r.total);
    } catch { setLoadError(true); } finally { setLoading(false); }
  };
  const filtersActive = !!q.trim() || !!status;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [q, status]);

  useEffect(() => {
    settingsService.listTeam().then((r) => setTeam(r.map((u) => ({ userUid: u.userUid, firstName: u.firstName, lastName: u.lastName, email: u.email })))).catch(() => {});
  }, []);

  useEffect(() => {
    if (contactsEnabled) contactsService.list({ limit: 200 }).then((r) => setContacts(r.items)).catch(() => {});
    else setContacts([]);
  }, [contactsEnabled]);

  useEffect(() => {
    if (timeEnabled) projectsService.currentTimer().then(setTimer).catch(() => {});
    else setTimer({ running: false, timer: null });
  }, [timeEnabled]);

  useEffect(() => {
    if (!timer.running || !timer.timer) return;
    setElapsed(timer.timer.elapsedSeconds);
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [timer]);

  const runningProjectName = timer.timer ? (projects.find((p) => p.projectUid === timer.timer!.projectUid)?.name ?? 'a project') : '';

  const stopTimer = async () => {
    try {
      await projectsService.stopTimer();
      toast.success('Timer stopped — time entry logged');
      setTimer({ running: false, timer: null });
      refresh();
    } catch { /* interceptor toasts */ }
  };

  const openNewModal = () => {
    setName(''); setDescription(''); setClientContactUid('');
    setBillingType('non_billable'); setBudgetAmount(''); setHourlyRate(''); setBudgetHours('');
    setCurrency(companyCurrency); setStartDate(''); setDueDate(''); setOwnerUserUid(''); setColor(COLOR_SWATCHES[0]);
    setModalOpen(true);
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      await projectsService.create({
        name: name.trim(),
        description: description.trim() || undefined,
        clientContactUid: clientContactUid || undefined,
        billingType,
        budgetAmount: budgetAmount ? parseFloat(budgetAmount) : undefined,
        hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
        budgetHours: budgetHours ? parseFloat(budgetHours) : undefined,
        currency,
        startDate: startDate || undefined,
        dueDate: dueDate || undefined,
        ownerUserUid: ownerUserUid || undefined,
        color,
      });
      toast.success(`${t('projects', 'singular')} created`);
      setModalOpen(false);
      refresh();
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  const fmtElapsed = (s: number) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return `${h > 0 ? `${h}:` : ''}${String(m).padStart(h > 0 ? 2 : 1, '0')}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <>
      <Topbar
        title={t('projects')}
        breadcrumb={`${t('operations')} / ${t('projects')}`}
        description={loading ? 'Loading…' : `${total} ${total === 1 ? t('projects', 'singular').toLowerCase() : t('projects').toLowerCase()}`}
        right={
          <div className="flex items-center gap-2">
            {timeEnabled && timer.running && timer.timer && (
              <button
                onClick={stopTimer}
                className="flex h-9 items-center gap-2 rounded-lg border border-success/40 bg-success-soft px-3 text-xs font-semibold text-success hover:brightness-105"
                title="Stop running timer"
              >
                <Clock size={13} className="animate-pulse" />
                {fmtElapsed(elapsed)} · {runningProjectName}
                <Square size={12} />
              </button>
            )}
            <Button variant="primary" iconLeft={<Plus size={14} />} onClick={openNewModal}>
              New {t('projects', 'singular').toLowerCase()}
            </Button>
          </div>
        }
      />

      <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SearchInput value={q} onChange={setQ} placeholder={`Search ${t('projects').toLowerCase()}…`} />
          <div className="flex flex-wrap gap-1.5">
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => setStatus(o.value)}
                className={clsx(
                  'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  status === o.value ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => <div key={i} className="skeleton h-52 rounded-2xl" />)}
          </div>
        )}

        {!loading && loadError && (
          <LoadErrorState what={`your ${t('projects').toLowerCase()}`} onRetry={() => void refresh()} />
        )}

        {!loading && !loadError && projects.length === 0 && filtersActive && (
          <NoMatchesState onClear={() => { setQ(''); setStatus(''); }} />
        )}

        {!loading && !loadError && projects.length === 0 && !filtersActive && (
          <EmptyState
            icon={<FolderKanban size={28} />}
            title={`No ${t('projects').toLowerCase()} yet`}
            description={`Track work, time and budgets for your ${t('contacts').toLowerCase()}. Create your first ${t('projects', 'singular').toLowerCase()} to get started.`}
            action={<Button onClick={openNewModal} iconLeft={<Plus size={14} />}>New {t('projects', 'singular').toLowerCase()}</Button>}
          />
        )}

        {!loading && !loadError && projects.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => <ProjectCard key={p.projectUid} project={p} clientName={contactName(p.clientContactUid)} />)}
          </div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => !busy && setModalOpen(false)} title={`New ${t('projects', 'singular').toLowerCase()}`} widthClass="max-w-2xl">
        <FormField label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Website revamp" autoFocus />
        </FormField>
        {contactsEnabled && <FormField label={t('contacts', 'singular')} hint={contacts.length ? undefined : `No ${t('contacts').toLowerCase()} yet`}>
          <Select value={clientContactUid} onChange={(e) => setClientContactUid(e.target.value)}>
            <option value="">— none —</option>
            {contacts.map((c) => <option key={c.contactUid} value={c.contactUid}>{[c.firstName, c.lastName].filter(Boolean).join(' ')}</option>)}
          </Select>
        </FormField>}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <FormField label="Billing type">
            <Select value={billingType} onChange={(e) => setBillingType(e.target.value as ProjectBillingType)}>
              <option value="non_billable">Non-billable</option>
              <option value="hourly">Hourly</option>
              <option value="fixed">Fixed price</option>
            </Select>
          </FormField>
          {billingType === 'hourly' && (
            <FormField label="Hourly rate">
              <Input type="number" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} placeholder="0.00" />
            </FormField>
          )}
          {billingType === 'fixed' && (
            <FormField label="Budget amount">
              <Input type="number" value={budgetAmount} onChange={(e) => setBudgetAmount(e.target.value)} placeholder="0.00" />
            </FormField>
          )}
          <FormField label="Currency">
            <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={4} />
          </FormField>
        </div>
        <FormField label="Budget hours" hint="Optional — used for progress tracking">
          <Input type="number" value={budgetHours} onChange={(e) => setBudgetHours(e.target.value)} placeholder="e.g. 40" />
        </FormField>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Start date">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </FormField>
          <FormField label="Due date">
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </FormField>
        </div>
        <FormField label="Owner">
          <Select value={ownerUserUid} onChange={(e) => setOwnerUserUid(e.target.value)}>
            <option value="">— unassigned —</option>
            {team.map((u) => <option key={u.userUid} value={u.userUid}>{[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}</option>)}
          </Select>
        </FormField>
        <FormField label="Description">
          <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormField>
        <FormField label="Colour">
          <div className="flex gap-2">
            {COLOR_SWATCHES.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)}
                className={clsx('h-7 w-7 rounded-full border-2 transition-transform', color === c ? 'scale-110 border-text-primary' : 'border-transparent')}
                style={{ background: c }} />
            ))}
          </div>
        </FormField>
        <div className="mt-2 flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={busy} disabled={busy || !name.trim()}>Create</Button>
        </div>
      </Modal>
    </>
  );
}

function ProjectCard({ project, clientName }: { project: Project; clientName: string | null }) {
  const summary = project.summary;
  const milestonesPct = summary && summary.milestones.total > 0 ? (summary.milestones.done / summary.milestones.total) * 100 : null;
  const overdue = summary?.overdue ?? false;

  return (
    <Link to={`/projects/${project.projectUid}`}>
      <Card hoverable className="relative h-full overflow-hidden !p-0">
        <div className="h-1.5 w-full" style={{ background: project.color }} />
        <div className="space-y-3 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate font-display text-[15px] font-semibold text-text-primary">{project.name}</h3>
              {clientName && (
                <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-text-muted">
                  <UsersIcon size={11} /> {clientName}
                </p>
              )}
            </div>
            <StatusPill status={project.status} variant={STATUS_VARIANT[project.status]} label={project.status.replace('_', ' ')} />
          </div>

          {project.dueDate && (
            <div className={clsx('flex items-center gap-1.5 text-xs', overdue ? 'font-semibold text-danger' : 'text-text-muted')}>
              <CalendarClock size={12} />
              Due {new Date(project.dueDate).toLocaleDateString()} {overdue && '· overdue'}
            </div>
          )}

          {summary && (
            <div className="space-y-2">
              {milestonesPct !== null && (
                <ProgressRow label={`Milestones ${summary.milestones.done}/${summary.milestones.total}`} pct={milestonesPct} />
              )}
              {summary.budgetUsedPct !== null && (
                <ProgressRow
                  label={project.billingType === 'hourly' && !project.budgetAmount ? 'Hours used' : 'Budget used'}
                  pct={summary.budgetUsedPct}
                  warn={summary.budgetUsedPct >= 100}
                />
              )}
              <p className="text-xs text-text-muted">
                {(summary.loggedMinutes / 60).toFixed(1)}h logged
                {project.billingType === 'hourly' && summary.billableAmount > 0 && ` · ${summary.billableAmount.toLocaleString()} ${project.currency}`}
              </p>
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
}

function ProgressRow({ label, pct, warn }: { label: string; pct: number; warn?: boolean }) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-2xs text-text-muted">
        <span>{label}</span>
        <span className={clsx('font-semibold tabular-nums', warn && 'text-danger')}>{Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-alt">
        <div className={clsx('h-full rounded-full', warn ? 'bg-danger' : 'bg-primary')} style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}
